import React, { Dispatch, forwardRef, useContext, useRef } from 'react';
import classes from './FindImagesModal.module.scss';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { topNavOffsetClassesGetter } from '../../../../helpers/topNavOffsetClasses';
import classNames from 'classnames';
import { useDimensions } from '../../../../hooks/useDimensions';
import { MediaGrid } from '../MediaGrid';
import { Loader } from '../../../../components/containers/Loader';
import { ButtonMenu } from '../../../../components/actions/ButtonMenu';
import { IconsEnum } from '../../../../constants/icons';
import {
  FindImagesFilterAction,
  FindImagesFilterType,
  MediaItem,
  MediaItemSelectedType
} from '../../../../models/media';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { FindImageFilterMenu } from '../FindImageFilterMenu';
import { SubmitHandler, UseFormReturn } from 'react-hook-form/dist/types/form';
import { MediaGridContext } from '../MediaGrid/MediaGrid.view';
import { Modal } from '../../../../components/containers/Modal';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { BreakpointsEnum } from '../../../../constants/breakpoints';
import { Search } from '../../../../components/forms/Search';
import { EmptyPlaceholder } from '../../../../components/containers/EmptyPlaceholder';
import noContentBox from 'assets/images/no-content-box.svg';
import { useForwardedRefTransformer } from '../../../../hooks/useForwardedRef';

export type FindImagesModalViewProps = {
  loadingInitial: boolean;
  loadingMore: boolean;
  mediaItems: MediaItem[];
  selectedMediaItems: MediaItemSelectedType[];
  setSelectedMediaItems: React.Dispatch<React.SetStateAction<MediaItemSelectedType[]>>;
  filters: FindImagesFilterType;
  dispatchFilters: Dispatch<FindImagesFilterAction>;
  showFilterModal: boolean;
  setShowFilterModal: (show: boolean) => void;
  setSearchTerm: (term: string) => void;
  onSubmit: SubmitHandler<{ term: string }>;
  fetchMoreMediaItemsHandler: () => void;
  formInstance: UseFormReturn;
  context: MediaGridContext;
  fileUploadHandler: (media: MediaItem[]) => void;
  shareItemAction: (media: MediaItem[]) => void;
  onClose: () => void;
  imagesIdsWithErrorSelected: MediaItemSelectedType[];
  setImagesIdsWithErrorSelected: React.Dispatch<React.SetStateAction<MediaItemSelectedType[]>>;
};

export const FindImagesModalView = forwardRef<HTMLDivElement, FindImagesModalViewProps>(
  (props: FindImagesModalViewProps, ref): JSX.Element => {
    const isFilterActive = Object.values(props.filters).some(f => !!f);
    const leftColRef = useRef<HTMLDivElement>(null);
    const rightColRef = useForwardedRefTransformer(ref);
    const topNavOffsetContext = useContext(TopNavContext);
    const topNavClasses = topNavOffsetClassesGetter(topNavOffsetContext.state.visibility, 0);
    const rightColumnWrapperClass = classNames(topNavClasses.vheight, classes.ModalRightSideColumn);
    const leftColumnClass = classNames(classes.LeftSideColumn);

    // Use bodyref for width and col count for grid
    const contentRef = useRef<HTMLDivElement>(null);
    const { width } = useDimensions(contentRef);

    const breakpoint = useBreakpoints();

    const isFindImagesContext = (context: MediaGridContext) =>
      context === MediaGridContext.FIND_IMAGES || context === MediaGridContext.POST_EDITOR_FIND_IMAGES;

    const leftColumnWrapperClass = classNames(classes.ModalLeftSideWrapper, 'cell', {
      'show-for-medium': !isFindImagesContext(props.context),
      'show-for-large': isFindImagesContext(props.context)
    });

    const modalFilterButtonMenuClass = classNames('margin-bottom-small-16', classes.ModalFilterButtonMenu, {
      'show-for-small hide-for-medium': !isFindImagesContext(props.context),
      'show-for-small hide-for-large': isFindImagesContext(props.context)
    });

    const mediaGrid = (
      <>
        {!props.loadingInitial && props.mediaItems.length ? (
          <MediaGrid
            mediaItems={props.mediaItems}
            primaryButtonAction={props.shareItemAction}
            primaryButtonTitle={'Share in Post'}
            secondaryButtonAction={props.fileUploadHandler}
            secondaryButtonTitle={'Add to Library'}
            context={props.context}
            parentWidth={width}
            selectedMediaItems={props.selectedMediaItems}
            setSelectedMediaItems={props.setSelectedMediaItems}
            imagesIdsWithErrorSelected={props.imagesIdsWithErrorSelected}
            setImagesIdsWithErrorSelected={props.setImagesIdsWithErrorSelected}
            isUnsplash={props.filters.source === 'unsplash'}
          />
        ) : !props.mediaItems.length ? (
          <EmptyPlaceholder title={'No results'} image={noContentBox} removeTopMargin>
            <div>Try changing your filters or search term</div>
          </EmptyPlaceholder>
        ) : (
          <Loader variation={'dark'} size={'large'} />
        )}
      </>
    );

    const findImagesFilterMenu = (
      <>
        <div className={classes.FindImagesSearchBox}>
          <form
            onSubmit={props.formInstance.handleSubmit(() =>
              props.onSubmit({ term: props.formInstance.getValues('term') })
            )}>
            <Search
              isFilter
              name={'term'}
              placeholder={'Enter keyword'}
              action={() => {
                const term = props.formInstance.getValues('term');
                props.setSearchTerm(term);
              }}
              onReset={() => props.setSearchTerm('')}
              formInstance={props.formInstance}
              rules={{}}
            />
          </form>
        </div>
        <div className='margin-top-small-24'>
        <FindImageFilterMenu
          loading={props.loadingInitial}
          isFilterActive={isFilterActive}
          filters={props.filters}
          dispatchFilters={props.dispatchFilters}
        />
        </div>

      </>
    );

    return (
      <Modal
        closeOnClickOutside={false}
        onClose={props.onClose}
        showCloseIcon={true}
        size={'fluid'}
        title={'Find Free Images'}
        scrollToBottomHandler={props.fetchMoreMediaItemsHandler}>
        <div ref={contentRef}>
          <div className={modalFilterButtonMenuClass}>
            {breakpoint === BreakpointsEnum.small ? (
              <>
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  action={() => props.setShowFilterModal(!props.showFilterModal)}
                  icon={{ side: 'left', value: IconsEnum.filter }}>
                  Filters
                </Button>
              </>
            ) : (
              <ButtonMenu
                isSquareButton={false}
                side={'left'}
                icon={IconsEnum.filter}
                dropdownMaximize={true}
                iconSide={'left'}
                elementType={'button'}
                label={'Filters'}
                size={'medium'}
                variant={'hollow'}>
                {findImagesFilterMenu}
              </ButtonMenu>
            )}
          </div>
          <div className={leftColumnWrapperClass}>
            <div className={leftColumnClass} ref={leftColRef}>
              <div>
                {findImagesFilterMenu}

              </div>
            </div>
          </div>
          <div className={rightColumnWrapperClass} ref={rightColRef}>
            <>
              {!props.loadingInitial ? <>{mediaGrid}</> : <Loader variation={'dark'} size={'large'} type={'inline'} />}
              {props.loadingMore && (
                <div className={classes.loadMoreContainer}>
                  <div className={classes.LoadingMore}>
                    <Loader variation={'dark'} size={'small'} type={'inline'} />
                  </div>
                </div>
              )}
            </>
          </div>
          {props.showFilterModal && breakpoint === BreakpointsEnum.small && (
            <Modal
              onClose={() => props.setShowFilterModal(false)}
              showCloseIcon={true}
              primaryButtonAction={() => {
                const term = props.formInstance.getValues('term');
                props.setSearchTerm(term);
                props.setShowFilterModal(false);
              }}
              primaryButtonColor={ButtonColorsEnum.primary}
              primaryButtonTitle={'Apply Filters'}
              isFilter
              title={'Filters'}>
              {findImagesFilterMenu}
            </Modal>
          )}
        </div>
      </Modal>
    );
  }
);

FindImagesModalView.displayName = 'FindImagesModalView';
